// Shared visual primitive for the carrier-state badge (PENDING /
// ANSWERED / REJECTED). Both the china carriers tab
// (`apps/web/src/features/requests/components/tabs/CarrierBidCard.tsx`)
// and the russia carriers tab (`apps/web/src/features/russia-requests/\
// components/tabs/RussiaCarrierBidCard.tsx`) render this component so
// the two surfaces share one visual language for carrier state.
//
// @aiPurpose accept either china's lowercase wire state
//   (`'pending' | 'answered' | 'rejected'`) or russia's uppercase Prisma
//   enum (`RequestCarrierState.PENDING | ANSWERED | REJECTED`) without
//   coupling callers to a normalised type. Internally lowercased so
//   `META[state]` indexes against a stable key.
// @aiInvariant the label and icon come from the `requests` i18n
//   namespace + the `carriers.state.*` keys; russia callers must mount
//   under an i18n provider that includes the `requests` resource
//   bundle (the app's root provider already does).

import { useTranslation } from 'react-i18next';
import type { RequestCarrierState } from '@magimp/shared';
import { Icon, type IconName } from '../../../ui/index.js';

type NormalisedCarrierState = 'pending' | 'answered' | 'rejected';

type Meta = {
  labelKey: 'carriers.state.pending' | 'carriers.state.answered' | 'carriers.state.rejected';
  icon: Extract<IconName, 'clock' | 'check' | 'x'>;
  color: string;
};

const META: Record<NormalisedCarrierState, Meta> = {
  pending: { labelKey: 'carriers.state.pending', icon: 'clock', color: 'var(--fg-2)' },
  answered: {
    labelKey: 'carriers.state.answered',
    icon: 'check',
    color: 'var(--status-success-fg)',
  },
  rejected: { labelKey: 'carriers.state.rejected', icon: 'x', color: 'var(--status-error-fg)' },
};

function normalise(state: string): NormalisedCarrierState {
  const lower = state.toLowerCase();
  if (lower === 'pending' || lower === 'answered' || lower === 'rejected') return lower;
  // INVARIANT: defensively treat any unknown wire state as pending so
  //   the pill never crashes on a future enum-additive change. Adding a
  //   new state is a non-breaking backend change; the UI surfaces the
  //   conservative neutral fallback until the catalog is extended.
  return 'pending';
}

export interface CarrierStatePillProps {
  state: RequestCarrierState | 'pending' | 'answered' | 'rejected' | string;
}

export function CarrierStatePill({ state }: CarrierStatePillProps) {
  const { t } = useTranslation('requests');
  const meta = META[normalise(String(state))];
  return (
    <span
      aria-label={t(meta.labelKey)}
      data-carrier-state={normalise(String(state))}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: meta.color,
      }}
    >
      <Icon name={meta.icon} size={12} />
      {t(meta.labelKey)}
    </span>
  );
}
